import React, { useState, useEffect } from 'react';
import { Cookie, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const CONSENT_KEY = 'barter_cookie_consent';
const CONSENT_VERSION = '1';

const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function loadConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(preferences) {
  const payload = {
    version: CONSENT_VERSION,
    timestamp: new Date()?.toISOString(),
    preferences,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
}

function applyGAConsent(analyticsAllowed) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    ad_storage: 'denied',
  });
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({ ...defaultPreferences });

  useEffect(() => {
    const stored = loadConsent();
    if (!stored) {
      // Slight delay so the page renders first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    // Re-apply GA consent on page load from stored prefs
    applyGAConsent(stored?.preferences?.analytics ?? false);
  }, []);

  const handleAcceptAll = () => {
    const prefs = { necessary: true, analytics: true, marketing: true };
    saveConsent(prefs);
    applyGAConsent(true);
    setVisible(false);
  };

  const handleRejectNonEssential = () => {
    const prefs = { necessary: true, analytics: false, marketing: false };
    saveConsent(prefs);
    applyGAConsent(false);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    applyGAConsent(preferences?.analytics);
    setVisible(false);
  };

  const togglePref = (key) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
    >
      <div className="max-w-4xl mx-auto bg-white border border-border rounded-lg shadow-organic-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-border">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <Cookie className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-heading">
              We use cookies
            </h2>
            <p className="mt-1 text-sm text-muted-foreground font-body leading-relaxed">
              We use cookies to improve your experience, analyse site traffic, and personalise content. 
              You can choose which cookies to allow. Read our{' '}
              <a
                href="/privacy-policy"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
              >
                Privacy Policy
              </a>{' '}
              for more information.
            </p>
          </div>
        </div>

        {/* Expandable preferences */}
        {showDetails && (
          <div className="px-4 sm:px-5 py-3 border-b border-border space-y-3 bg-muted/30">
            {/* Necessary */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground font-heading flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-success" />
                  Strictly Necessary
                </p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">
                  Required for the site to function. Cannot be disabled.
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success font-caption">
                  Always on
                </span>
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground font-heading">Analytics</p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">
                  Helps us understand how visitors use the site (Google Analytics).
                </p>
              </div>
              <button
                onClick={() => togglePref('analytics')}
                role="switch"
                aria-checked={preferences?.analytics}
                className={`flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                  preferences?.analytics ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    preferences?.analytics ? 'translate-x-4' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground font-heading">Marketing</p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">
                  Used to show relevant ads and track campaign effectiveness.
                </p>
              </div>
              <button
                onClick={() => togglePref('marketing')}
                role="switch"
                aria-checked={preferences?.marketing}
                className={`flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                  preferences?.marketing ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    preferences?.marketing ? 'translate-x-4' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-4 sm:p-5">
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground font-body transition-colors px-3 py-2 rounded-md hover:bg-muted/50 sm:mr-auto"
          >
            {showDetails ? (
              <>Manage preferences <ChevronUp className="w-4 h-4" /></>
            ) : (
              <>Manage preferences <ChevronDown className="w-4 h-4" /></>
            )}
          </button>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleRejectNonEssential}
              className="px-4 py-2 text-sm font-medium font-heading text-foreground bg-white border border-border rounded-md hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
            >
              Reject non-essential
            </button>

            {showDetails ? (
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 text-sm font-medium font-heading text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
              >
                Save preferences
              </button>
            ) : (
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium font-heading text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
              >
                Accept all
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
